import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ProductVisual } from "./ProductVisual";

const AMENITIES_IMG =
  "https://images.unsplash.com/photo-1711060001575-2cfa3c5a2bfa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwaG90ZWwlMjBhbWVuaXRpZXMlMjBib3R0bGVzfGVufDB8fHx8MTc4NzkxNDY5NHww&ixlib=rb-4.1.0&q=85";
const MACHINES_IMG =
  "https://images.unsplash.com/photo-1784622526684-e27f1e0329bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwY2xlYW5pbmclMjBtYWNoaW5lfGVufDB8fHx8MTc4NzkxNDY5NHww&ixlib=rb-4.1.0&q=85";

const META = {
  "guest-amenities": { visual: "dispenser", image: AMENITIES_IMG },
  "fragrance-solutions": { visual: "oil", image: null },
  "housekeeping-chemicals": { visual: "jerrycan", image: null },
  "housekeeping-tools": { visual: "mop", image: null },
  "housekeeping-machines": { visual: "machine", image: MACHINES_IMG },
};

export const Categories = ({ categories, onSelect }) => {
  const handle = (slug) => {
    onSelect(slug);
  };

  return (
    <section id="categories" data-testid="categories-section" className="max-w-[1500px] mx-auto px-5 sm:px-6 lg:px-12 py-20 sm:py-28">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 sm:mb-16"
      >
        <h2 className="font-clash text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-none text-[#221533]">
          Five lines.<br />
          <span className="italic text-[#8E5A9E]">One standard.</span>
        </h2>
        <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#221533]/45">
          Category → Product → Order
        </p>
      </motion.div>

      <div>
        {categories.map((cat, i) => {
          const meta = META[cat.slug] || {};
          return (
            <motion.button
              key={cat.slug}
              data-testid={`category-row-${cat.slug}`}
              onClick={() => handle(cat.slug)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
              className="group w-full text-left border-t border-[#221533]/10 last:border-b py-8 sm:py-10 lg:py-14 grid grid-cols-12 gap-4 sm:gap-6 items-center hover:bg-[#8E5A9E]/[0.03] transition-colors duration-500 px-2 lg:px-6"
            >
              <span className="col-span-2 lg:col-span-1 font-mono2 text-sm text-[#221533]/40">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="col-span-9 lg:col-span-6">
                <h3 className="font-clash text-2xl sm:text-4xl lg:text-6xl font-medium tracking-tight text-[#221533] group-hover:translate-x-3 group-hover:text-[#74427F] transition-[transform,color] duration-500">
                  {cat.name}
                </h3>
                <p className="mt-2 text-sm lg:text-base text-[#221533]/50 font-light">{cat.tagline}</p>
              </div>
              <div className="hidden lg:flex col-span-4 justify-center">
                <div className="relative w-56 h-40 overflow-hidden border border-[#221533]/10 rounded-lg spotlight bg-white">
                  {meta.image ? (
                    <img
                      src={meta.image}
                      alt={cat.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700"
                    />
                  ) : (
                    <ProductVisual
                      type={meta.visual}
                      className="absolute inset-0 m-auto h-[85%] w-auto opacity-80 grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700"
                    />
                  )}
                </div>
              </div>
              <span className="col-span-1 flex justify-end">
                <ArrowUpRight
                  size={26}
                  className="text-[#221533]/30 group-hover:text-[#8E5A9E] group-hover:rotate-45 transition-[transform,color] duration-500"
                />
              </span>
            </motion.button>
          );
        })}
      </div>
    </section>
  );
};
